/**
 * DisplayController - Makes display decision and controls widget rendering
 *
 * This component orchestrates the complete widget display decision by:
 * 0. Checking for critical initialization errors (no domain, service creation failed, etc.) - FAIL CLOSED
 * 1. Waiting for analytics (PostHog) to be ready (required for tracking, not for traffic control)
 * 2. Checking traffic control (Supabase-based bucketing via siteConfig)
 * 3. Checking display rules (URL patterns, device compatibility)
 * 4. Tracking rw_posthog_initialized event with decision data
 * 5. Rendering RoseWidget with appropriate displayMode if allowed
 *
 * Architecture:
 * - Sits between providers (ConfigProvider, AnalyticsProvider) and RoseWidget
 * - Has access to both contexts needed for display decision
 * - Eliminates cross-component state synchronization
 * - Ensures proper event sequencing: PostHog ready → display decision → widget render → tracking
 *
 * Important:
 * - CRITICAL: Widget will NOT render if there are fatal initialization errors (fail closed)
 * - PostHog must be ready before widget initializes (for analytics tracking)
 * - Traffic control is Supabase-based (not PostHog-based)
 * - PostHog is used for clientId generation (stable bucketing) and event tracking
 */

import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { NetworkAdapter } from '../adapters/network';
import type { StorageAdapter } from '../adapters/storage';
import {
    Analytics,
    capturePageviewOnce,
    getPostHog,
    hasPostHogInitializedEventBeenSent,
    setAnalyticsContext,
} from '../analytics';
import { resolvePageLoadEventPolicy } from '../analytics/pageLoadEventPolicy';
import { getDeploymentTarget, isBackofficePlayground } from '../config/deployment';
import {
    CLIENT_API_ACTIVATION_EVENT,
    FORM_SUBMIT_ACTIVATION_EVENT,
} from '../config/PostConversionFormSubmitTracker';
import { shouldUseFormAssistant, shouldUsePostConversion } from '../config/SiteConfigManager';
import { useConfig } from '../config/unified';
import { useAnalytics } from '../context/AnalyticsContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { ChatStorageService } from '../services/chatStorage';
import type { EvaluationSubmissionOverride } from '../services/evaluation';
import { roseStorage } from '../services/rose-storage';
import type { StaffHoldoutOptOutConfig } from '../types/configs.generated';
import { logger } from '../utils/core/logger';
import { subscribeToUrlChanges } from '../utils/web/urlChange';
import { canInitializeWidget, shouldDisplayOnCurrentUrl } from '../utils/widgetInit';
import {
    activeClientControlSplitPct,
    computeClientAllocation,
    resolveExperimentCohortDecision,
} from '../utils/widgetInit/experimentCohorts';
import RoseWidget, { type RoseWidgetRef } from './RoseWidget';

export interface DisplayControllerProps {
    apiUrl: string;
    apiKey?: string;
    forceApiUrl?: boolean; // When true, apiUrl takes precedence over backoffice config
    siteName?: string;
    domain: string;
    storageAdapter: StorageAdapter;
    networkAdapter: NetworkAdapter;
    forceDisplay?: boolean;
    enableEvaluation?: boolean;
    submitEvaluationOverride?: EvaluationSubmissionOverride;
    hiddenZoneContainer?: HTMLElement;
    formFactor?: 'default' | 'copilot'; // Force form factor (overrides auto-detection)
    conversationLanguage?: string;
    visitorIp?: string;
    isFormAssistant?: boolean; // Force form assistant behavior (overrides auto-detection)
    isPostConversion?: boolean; // Force post-conversion behavior (overrides auto-detection)
    /**
     * Playground device-preview only: simulate a viewport width so the widget's
     * JS width breakpoints (mobile full-height chat layout) trigger at the chosen
     * device size instead of reading the host window. Undefined in production.
     */
    previewViewportWidth?: number;
}

const emitDisplayDecision = (allowed: boolean, reason?: string) => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
        new CustomEvent('inboundx_display_decision', {
            detail: { allowed, reason },
        })
    );
};

interface PostHogDistinctIdProvider {
    get_distinct_id?: () => string | null | undefined;
}

export const DisplayController = forwardRef<RoseWidgetRef, DisplayControllerProps>(
    (
        {
            apiUrl,
            apiKey,
            forceApiUrl = false,
            siteName,
            domain,
            storageAdapter,
            networkAdapter,
            forceDisplay = false,
            enableEvaluation = false,
            submitEvaluationOverride,
            hiddenZoneContainer,
            formFactor: forcedFormFactor,
            conversationLanguage,
            visitorIp,
            isFormAssistant: forcedIsFormAssistant,
            isPostConversion: forcedIsPostConversion,
            previewViewportWidth,
        },
        roseWidgetRef
    ) => {
        const { resolver } = useConfig();
        const { isLoaded, error, errorType } = useSiteConfig();
        const { isAnalyticsReady } = useAnalytics();
        const siteConfig = useMemo(
            () =>
                resolver
                    ? {
                          domain: resolver.domain,
                          traffic_allocated: resolver.traffic_control.traffic_percentage,
                          widget_display_mode: resolver.traffic_control.widget_display_mode,
                          display_only_on_urls: resolver.traffic_control.display_only_on_urls,
                          custom_config: {
                              enable_mobile: resolver.traffic_control.enable_mobile,
                              page_display_rules: resolver.traffic_control.page_display_rules,
                              data_layer_display_conditions:
                                  resolver.traffic_control.data_layer_display_conditions,
                          },
                      }
                    : null,
            [resolver]
        );

        const [displayDecision, setDisplayDecision] = useState<{
            allowed: boolean;
            bootstrapAllowed: boolean;
            displayMode: 'normal' | 'hidden_zone';
            formFactor: 'default' | 'copilot';
            isFormAssistant: boolean;
            isPostConversion: boolean;
            reason?: string;
        } | null>(null);

        // "Initial display decision has completed", NOT "the init event was sent".
        // The two diverged when emission became conditional: on a URL-ineligible page
        // (or a re-mount where the event already went out) nothing is emitted, but the
        // decision still has to latch so the initial effect stops re-entering and the
        // URL-change effect below is allowed to run.
        const [hasMadeInitialDecision, setHasMadeInitialDecision] = useState(false);
        const displayDecisionRef = useRef(displayDecision);
        displayDecisionRef.current = displayDecision;
        // Kept so the URL-change effect can re-emit rw_posthog_initialized with the same
        // decision payload when the initial effect suppressed it.
        const initPropertiesRef = useRef<Record<string, unknown>>({});
        const [currentUrl, setCurrentUrl] = useState(
            typeof window !== 'undefined' ? window.location.href : ''
        );
        const currentUrlRef = useRef(currentUrl);
        currentUrlRef.current = currentUrl;

        useEffect(() => {
            return subscribeToUrlChanges((url) => {
                setCurrentUrl(url);
            });
        }, []);

        // Make display decision once analytics is ready
        // CRITICAL: Track rw_posthog_initialized and WAIT for it to complete BEFORE setting displayDecision
        // This ensures proper event ordering: rw_posthog_initialized → widget renders → rw_widget_impression
        useEffect(() => {
            // CRITICAL: Wait for analytics to be ready before any widget initialization
            // We need PostHog ready for analytics tracking AND for stable clientId generation
            // Traffic control is Supabase-based, but uses PostHog distinct_id for bucketing
            if (!isAnalyticsReady) {
                logger.debug('🌍 4️⃣ ⏸️ Waiting for analytics (PostHog) before display check', {
                    isAnalyticsReady,
                    reason: 'PostHog must be ready for analytics tracking and clientId generation',
                });
                return;
            }

            // Verify PostHog is actually available (defensive check)
            const posthog = getPostHog();
            if (!posthog) {
                logger.warn('⚠️ PostHog not available despite isAnalyticsReady = true');
                return;
            }

            // Skip if the initial decision already landed (prevents re-entry on dependency changes)
            if (hasMadeInitialDecision) {
                return;
            }

            // Get current URL for display rules check
            const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
            const isForcedBackofficePreview = forceDisplay && isBackofficePlayground();
            const distinctId = (posthog as PostHogDistinctIdProvider).get_distinct_id?.();
            if (!distinctId && !isForcedBackofficePreview) {
                logger.warn('⚠️ PostHog distinct_id not available despite isAnalyticsReady = true');
                return;
            }

            // Make complete display decision
            // Layer 1: Traffic control (Supabase-based, uses PostHog for stable clientId internally)
            // Layer 2: Display rules (URL patterns, device compatibility)
            // Note: Page display rules (hidden/minimized) are resolved by RoseWidget via currentUrl prop
            // Note: canInitializeWidget will call getPostHog() internally when needed

            // Carve model (IX-3984): when a client experiment is active, widen the
            // traffic-layer threshold to the carve ceiling (display + control) so
            // control-bucket visitors pass traffic control and reach cohort
            // resolution (which then hides + tags them). Display stays = traffic %.
            const clientControlSplit = resolver
                ? activeClientControlSplitPct(resolver.domainExperiment)
                : 0;
            const allocation =
                clientControlSplit > 0
                    ? computeClientAllocation(
                          resolver?.traffic_control.traffic_percentage,
                          clientControlSplit
                      )
                    : null;
            const effectiveSiteConfig =
                siteConfig && allocation
                    ? { ...siteConfig, traffic_allocated: allocation.ceilingBuckets }
                    : (siteConfig ?? null);

            const decision = canInitializeWidget(effectiveSiteConfig, {
                forceDisplay,
                currentUrl,
            });
            // A manual `trafficControlOverride: true` is a staff debugging escape
            // hatch ("show me the widget here"). Traffic control is only the first
            // gate — without this the experiment control cohort still hides the
            // widget, and the override reads as broken (worse in the Chrome
            // extension, where forceDisplay already skips traffic control so the
            // override never even runs).
            const overriddenToShow = roseStorage.getTrafficControlOverride() === true;
            const cohortDecision =
                !isForcedBackofficePreview &&
                !overriddenToShow &&
                resolver &&
                distinctId &&
                decision.details.trafficControlPassed
                    ? resolveExperimentCohortDecision({
                          distinctId,
                          trafficBucket: decision.details.userBucket,
                          trafficPercentage: resolver.traffic_control.traffic_percentage,
                          staffHoldout: resolver.staffExperiment,
                          staffHoldoutOptOut:
                              resolver.getConfig<StaffHoldoutOptOutConfig>('staff_holdout_opt_out'),
                          clientExperiment: resolver.domainExperiment,
                      })
                    : {
                          widgetAllowed: true,
                          properties: {},
                      };
            const cohortAllowed = cohortDecision.widgetAllowed;
            const allowed = isForcedBackofficePreview || (decision.allowed && cohortAllowed);
            const reason = isForcedBackofficePreview
                ? 'Backoffice playground forces widget display'
                : cohortAllowed
                  ? decision.reason
                  : cohortDecision.reason;
            setAnalyticsContext(cohortDecision.properties);

            // Determine display mode (hidden_zone from site config only)
            // Page display rules (hidden/minimized/displayed) are resolved by RoseWidget internally
            const resolveDisplayMode = (): 'normal' | 'hidden_zone' => {
                if (forceDisplay) return 'normal';
                return siteConfig?.widget_display_mode === 'hidden_zone_click'
                    ? 'hidden_zone'
                    : 'normal';
            };
            const resolvedDisplayMode = resolveDisplayMode();

            // Determine form factor:
            // - forcedFormFactor: If explicitly provided via props, use it (for testing/demos)
            // - copilotModeOverride: sessionStorage flag set by internal content recommendation links
            // - 'copilot': Used on CTA pages when form_assistant feature flag is enabled
            // - 'default': Standard widget mode (search bar, centered positioning)
            const isFormAssistant = forcedIsFormAssistant ?? shouldUseFormAssistant(domain);
            const isPostConversion = forcedIsPostConversion ?? shouldUsePostConversion(domain);
            // Override key must match writer in ContentRecommendationCard / MarkdownRenderer:
            // resolver.domain (canonical config domain), falling back to hostname so
            // writes and reads stay aligned across www / apex / chrome-plugin defaults.
            const overrideDomain = resolver?.domain ?? domain;
            const copilotModeOverride = ChatStorageService.getCopilotModeOverride(overrideDomain);
            if (copilotModeOverride) {
                ChatStorageService.setCopilotModeOverride(overrideDomain, false);
            }
            const formFactor =
                forcedFormFactor ??
                (copilotModeOverride ? 'copilot' : undefined) ??
                (isFormAssistant ? 'copilot' : 'default');

            logger.info('🌍 4️⃣ Display decision made', {
                allowed,
                reason,
                trafficControlPassed: decision.details.trafficControlPassed,
                urlAllowed: decision.details.urlAllowed,
                deviceAllowed: decision.details.deviceAllowed,
                cohortAllowed,
                ...cohortDecision.properties,
                displayMode: resolvedDisplayMode,
                formFactor,
                isFormAssistant,
                isPostConversion,
                forceDisplay,
                isForcedBackofficePreview,
                currentUrl,
                deploymentTarget: getDeploymentTarget(),
            });

            // Track PostHog initialization event with complete decision data
            // CRITICAL: Wait for tracking to complete BEFORE setting displayDecision
            // This ensures rw_posthog_initialized is sent BEFORE RoseWidget mounts and tracks rw_widget_impression
            // Fail closed: If tracking fails, widget does NOT render (analytics integrity maintained)
            logger.debug(
                '📊 Tracking rw_posthog_initialized - waiting for completion before allowing widget to render'
            );

            const initProperties = {
                deploymentTarget: getDeploymentTarget(),
                displayAllowed: allowed,
                displayReason: reason,
                ...decision.details,
                ...cohortDecision.properties,
            };
            logger.debug('📊 rw_posthog_initialized properties:', initProperties);
            initPropertiesRef.current = initProperties;

            // The event is the experiment exposure anchor (analytics.experiment_anchor_events
            // ingests it and reads the variant assignment off it), so it must fire at most once
            // per page load. The component-local latch below is not enough: SPA DOM recovery in
            // main.tsx re-inits with forceInit, which unmounts and remounts this component while
            // the PostHog provider — being module-scope — survives. Consult the provider's flag,
            // which has true page-load lifetime.
            const alreadySent = hasPostHogInitializedEventBeenSent();
            if (alreadySent) {
                logger.debug(
                    '⏭️ rw_posthog_initialized already sent this page load - skipping duplicate'
                );
            }

            // On a page the widget may not display on, the event is pure cost: the
            // experiment mart discards url_allowed=false anchors anyway.
            const suppressed = !resolvePageLoadEventPolicy({
                urlEligible: decision.details.urlAllowed,
                hostInScope: true,
                isTrackedSurface: false,
                mode: resolver?.analytics.page_load_event_suppression ?? 'off',
            }).trackInitEvent;
            if (suppressed) {
                logger.debug(
                    '⏭️ rw_posthog_initialized suppressed - widget not URL-eligible on this page'
                );
            }

            (alreadySent || suppressed
                ? Promise.resolve()
                : Analytics.trackPostHogInitialized(initProperties)
            ).then(() => {
                logger.debug(
                    alreadySent || suppressed
                        ? '✅ rw_posthog_initialized not needed - now allowing widget to render'
                        : '✅ rw_posthog_initialized sent successfully - now allowing widget to render'
                );

                // Latch the decision (not the emission - see hasMadeInitialDecision above)
                setHasMadeInitialDecision(true);

                // Set display decision to allow widget to render
                // RoseWidget handles all visibility signaling to external integrations (AI Sections)
                setDisplayDecision({
                    allowed,
                    bootstrapAllowed:
                        isForcedBackofficePreview ||
                        (decision.details.trafficControlPassed &&
                            decision.details.deviceAllowed &&
                            cohortAllowed),
                    displayMode: resolvedDisplayMode,
                    formFactor,
                    isFormAssistant,
                    isPostConversion,
                    reason,
                });
                emitDisplayDecision(allowed, reason);

                if (!allowed) {
                    logger.info('🌍 4️⃣ ❌ Widget blocked:', reason);
                } else if (resolvedDisplayMode !== 'normal') {
                    logger.info(`🌍 4️⃣ ⏸️ Display mode: ${resolvedDisplayMode}`);
                } else {
                    logger.info('🌍 4️⃣ ✅ All checks passed - showing widget');
                }
            });
            // No .catch() - fail closed: if tracking fails, widget doesn't render
        }, [
            isAnalyticsReady,
            siteConfig,
            forceDisplay,
            hasMadeInitialDecision,
            domain,
            resolver,
            forcedFormFactor,
            forcedIsFormAssistant,
            forcedIsPostConversion,
        ]);

        // IX-2646: When a qualifying form submit is detected on a landing page
        // without a thank-you URL (e.g. Unbounce inline-AJAX forms), re-evaluate
        // the display decision so the widget switches into post-conversion mode
        // on the same page and auto-opens the qualification flow.
        // ⚠️ NOT YET TESTED IN PRODUCTION — first trial is on Matera. No
        // automated coverage for this listener yet; verify manually before
        // enabling on-form-submit on a new client.
        useEffect(() => {
            if (typeof window === 'undefined') return;
            const applyActivation = (source: 'form_submit' | 'client_api') => {
                const prevDecision = displayDecisionRef.current;
                if (!prevDecision) return;

                // Invariant: the widget must already be displayed on this page for a
                // form-submit activation to flip it into post-conversion mode. If the
                // visitor was excluded (traffic-control bucket, display_only_on_urls,
                // page_display_rules), we must not force the widget to appear — the
                // operator needs to add the landing page to the widget's display rules.
                if (source === 'form_submit' && !prevDecision.allowed) {
                    logger.warn(
                        '⚠️ DisplayController: form-submit activation ignored — widget is not displayed on this page. Add the landing page to display_only_on_urls / page_display_rules.'
                    );
                    return;
                }

                const isPostConversion = forcedIsPostConversion ?? shouldUsePostConversion(domain);
                const nextAllowed = source === 'client_api' ? true : prevDecision.allowed;
                if (
                    isPostConversion === prevDecision.isPostConversion &&
                    nextAllowed === prevDecision.allowed
                ) {
                    return;
                }
                logger.info(
                    '🎯 DisplayController: post-conversion activation → recomputing display',
                    { isPostConversion, source }
                );
                setDisplayDecision({
                    ...prevDecision,
                    allowed: nextAllowed,
                    reason: nextAllowed ? undefined : prevDecision.reason,
                    isPostConversion,
                });
                if (source === 'client_api') {
                    emitDisplayDecision(nextAllowed, nextAllowed ? undefined : prevDecision.reason);
                }
            };

            const formSubmitHandler = () => applyActivation('form_submit');
            const clientApiHandler = () => applyActivation('client_api');

            window.addEventListener(FORM_SUBMIT_ACTIVATION_EVENT, formSubmitHandler);
            window.addEventListener(CLIENT_API_ACTIVATION_EVENT, clientApiHandler);
            return () => {
                window.removeEventListener(FORM_SUBMIT_ACTIVATION_EVENT, formSubmitHandler);
                window.removeEventListener(CLIENT_API_ACTIVATION_EVENT, clientApiHandler);
            };
        }, [domain, forcedIsPostConversion]);

        useEffect(() => {
            if (!hasMadeInitialDecision) return;
            if (!isAnalyticsReady) return;
            const prevDecision = displayDecisionRef.current;
            if (!prevDecision) return;

            const url = currentUrl || (typeof window !== 'undefined' ? window.location.href : '');
            // IX-2187: Always re-evaluate display rules on URL change.
            // shouldDisplayOnCurrentUrl() enforces display_only_on_urls and page_display_rules,
            // hiding the widget when SPA navigation lands on a non-allowed page.
            // DOM recovery after SPA route changes is handled separately in main.tsx.
            logger.debug('DisplayController: URL change detected, recomputing display decision', {
                currentUrl: url,
            });

            // Page display rules (hidden/minimized/displayed) are resolved by RoseWidget internally
            const resolveDisplayMode = (): 'normal' | 'hidden_zone' => {
                if (forceDisplay) return 'normal';
                return siteConfig?.widget_display_mode === 'hidden_zone_click'
                    ? 'hidden_zone'
                    : 'normal';
            };

            const isFormAssistant = forcedIsFormAssistant ?? shouldUseFormAssistant(domain);
            const isPostConversion = forcedIsPostConversion ?? shouldUsePostConversion(domain);
            // Preserve current formFactor across SPA navigations — only the initial effect sets it
            const formFactor = prevDecision.formFactor;
            const bootstrapAllowed = prevDecision.bootstrapAllowed;
            const isForcedBackofficePreview = forceDisplay && isBackofficePlayground();
            const pageAllowed =
                isForcedBackofficePreview ||
                shouldDisplayOnCurrentUrl(siteConfig ?? null, { currentUrl: url });
            const allowed = bootstrapAllowed && pageAllowed;
            const reason = isForcedBackofficePreview
                ? undefined
                : bootstrapAllowed
                  ? pageAllowed
                      ? undefined
                      : 'URL not allowed by site configuration'
                  : prevDecision.reason;

            const applyDecision = () => {
                setDisplayDecision({
                    allowed,
                    bootstrapAllowed,
                    displayMode: resolveDisplayMode(),
                    formFactor,
                    isFormAssistant,
                    isPostConversion,
                    reason,
                });
                emitDisplayDecision(allowed, reason);
            };

            // The visitor started on a page where the page-load events were suppressed and
            // has now navigated onto an eligible one, so nothing has been emitted yet for
            // this page load. Emit the anchor (and the $pageview posthog-js will never fire
            // again by itself) before rendering the widget.
            if (!pageAllowed || hasPostHogInitializedEventBeenSent()) {
                applyDecision();
                return;
            }

            // CRITICAL: await the anchor. setDisplayDecision synchronously renders
            // RoseWidget, which gates rw_widget_impression on this same flag — firing and
            // forgetting would drop the impression or order it before its anchor.
            Analytics.trackPostHogInitialized({
                ...initPropertiesRef.current,
                displayAllowed: allowed,
                displayReason: reason,
                urlAllowed: true,
                currentUrl: url,
            }).then(() => {
                capturePageviewOnce();
                // A further navigation landed while we were in flight; that effect run owns
                // the decision now.
                if (currentUrlRef.current !== url) return;
                applyDecision();
            });
        }, [
            currentUrl,
            hasMadeInitialDecision,
            isAnalyticsReady,
            siteConfig,
            forceDisplay,
            forcedIsFormAssistant,
            forcedIsPostConversion,
            domain,
        ]);

        // CRITICAL: Check for initialization errors first - widget should NOT render if there are fatal errors
        if (isLoaded && error) {
            // Error handling is in ConfigProvider:
            // - Domain not configured → logged as info (no Sentry) via DomainNotConfiguredError
            // - Config load failed → logged as error with Sentry
            // No need to duplicate reporting here.
            logger.debug('DisplayController: Widget not rendering due to initialization error', {
                error,
                errorType,
            });
            return null;
        }

        // Wait for analytics and display decision
        if (!isAnalyticsReady || !displayDecision) {
            logger.debug('⏸️ WidgetDisplayController: Waiting for initialization', {
                isAnalyticsReady,
                hasDecision: !!displayDecision,
            });
            return null;
        }

        // If blocked, don't render widget
        if (!displayDecision.allowed) {
            logger.debug('❌ WidgetDisplayController: Widget blocked, not rendering', {
                reason: displayDecision.reason,
            });
            return null;
        }

        // Render RoseWidget with display mode and form factor
        logger.debug('✅ DisplayController: Rendering RoseWidget', {
            displayMode: displayDecision.displayMode,
            formFactor: displayDecision.formFactor,
            isFormAssistant: displayDecision.isFormAssistant,
            isPostConversion: displayDecision.isPostConversion,
        });

        return (
            <RoseWidget
                ref={roseWidgetRef}
                apiUrl={apiUrl}
                apiKey={apiKey}
                forceApiUrl={forceApiUrl}
                siteName={siteName}
                domain={domain}
                storageAdapter={storageAdapter}
                networkAdapter={networkAdapter}
                forceDisplay={forceDisplay}
                enableEvaluation={enableEvaluation}
                submitEvaluationOverride={submitEvaluationOverride}
                displayMode={displayDecision.displayMode}
                currentUrl={currentUrl}
                formFactor={displayDecision.formFactor}
                isFormAssistant={displayDecision.isFormAssistant}
                isPostConversion={displayDecision.isPostConversion}
                hiddenZoneContainer={hiddenZoneContainer}
                conversationLanguage={conversationLanguage}
                visitorIp={visitorIp}
                previewViewportWidth={previewViewportWidth}
            />
        );
    }
);

DisplayController.displayName = 'DisplayController';
